import React from 'react';
import { defineMessages } from 'react-intl';
import { takeEvery, call, select, all, put } from 'redux-saga/effects';

import { closeModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { EMAIL_TYPES } from 'constants/logic';
import URLS from 'constants/urls';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import { transformKeysToSnakeCase } from 'helpers/core';
import Segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';

import { SKU_ANALYTICS_EVENTS } from '../Items/SkuAnalytics.constants';
import { getSkuVenueSource } from '../Items/SkuVenueSource.helpers';
import sharedMessages from './SkuOrder.messages';

// Keep 5xx on in-app flows (banner + modal) instead of the full-page InternalError screen.
const HTTP_OPTIONS = { ignoreInternalError: true };

const messages = defineMessages({
  emailSentSuccess: {
    id: 'sku-orders.email-sent-success',
    defaultMessage: 'Email sent to {customerName}',
  },
  emailSentSuccessGeneric: {
    id: 'sku-orders.email-sent-success-generic',
    defaultMessage: 'Email sent successfully.',
  },
  emailErrorTitle: {
    id: 'sku-orders.email-error-title',
    defaultMessage: '<strong>We were unable to send your email</strong>',
  },
});

export const EMAIL_ORDER_CUSTOMER = 'SKU_ORDERS/EMAIL_ORDER_CUSTOMER';

export const emailOrderCustomer = (values, formAsyncFns) => ({
  type: EMAIL_ORDER_CUSTOMER,
  formValues: values,
  formAsyncFns,
});

export function* doEmailOrderCustomer({ httpClient, intl }, action) {
  const { message: messageContent, recipients, subjectValues, userName, orderId, surface } = action.formValues;
  const { resolveSubmit, rejectSubmit } = action.formAsyncFns;
  const venue = yield select(selectActiveVenue);
  const { venueId } = venue;
  const source = getSkuVenueSource(venue);
  const subject = intl.formatMessage(sharedMessages.emailModalSubject, subjectValues);

  const payload = transformKeysToSnakeCase({
    email: {
      messageType: EMAIL_TYPES.GENERIC_TO_SOME_USERS,
      messageContent: messageContent.replace(/\n/g, '<br />'),
      subject,
    },
    recipients,
  });

  try {
    yield call([httpClient, httpClient.post], URLS.sku.messageMembers(venueId), payload, HTTP_OPTIONS);
    Segment.event(SKU_ANALYTICS_EVENTS.USER_CONTACTED, { venueId, source, orderId, surface });
    yield all([
      call(resolveSubmit),
      put(closeModal()),
      put(
        userName
          ? createSuccessSnackbar(messages.emailSentSuccess, { customerName: userName })
          : createSuccessSnackbar(messages.emailSentSuccessGeneric)
      ),
    ]);
  } catch (error) {
    yield all([
      call(rejectSubmit),
      put(closeModal()),
      put(
        createErrorSnackbar(
          messages.emailErrorTitle,
          { strong: chunks => <strong>{chunks}</strong> },
          undefined,
          getApiErrorSnackbarDescription(error)
        )
      ),
    ]);
  }
}

export function* watchEmailOrderCustomer(dependencies) {
  yield takeEvery(EMAIL_ORDER_CUSTOMER, doEmailOrderCustomer, dependencies);
}
